import { useEffect, useState } from 'react';
import { useQuoteDetailStore } from '@/cms/store/quote/QuoteDetail';
import { canEditQuoteData, canModifyQuotePrice } from '@/cms/util/quote';
import { QuoteRecoverySourceEnum } from '@jove-frontend/enums';
import {
  CMR_COVER,
  COVER_INFO_MAP,
  ContractEstimatedIncomePerInfoMap,
  EMPLOYERS_LIABILITY,
  EXCESS_LEVEL,
  GOODS_IN_TRANSIT,
  PROFESSIONAL_INDEMNITY,
  PROPERTY_COVER_TYPE_LIST,
  PUBLIC_LIABILITY,
  PriceFromEnum,
  PromotionEntityType,
  QuoteSourceEnum,
  QuoteStatus,
  YesNoEnum,
  declarationsOptionsMap,
  getAllQuoteQuestionInfoMap,
  getChubbContractRateSwitchTypeMap,
  getDeclarationsOptionsMap,
  greatAmericanBusinessInsuranceClassServiceMap,
  greateAmericanTypeOfOperationCourier,
  swapTheValuesOfTheYesAndNoOptions,
  useQuotePriceDetailInfo,
} from '@jove-frontend/uikit';
import dayjs from 'dayjs';
import { has, isArray, isBoolean } from 'lodash';
import { useFormContext } from 'react-hook-form';

export const useQuoteDetail = () => {
  const { getValues } = useFormContext() || {};
  const [{ quotePriceInfo }] = useQuoteDetailStore((store) => [store.state]);
  const [quoteRestorePageUrl, setQuoteRestorePageUrl] = useState('');
  const { generateGreatAmericanPriceSummaryInfoList } = useQuotePriceDetailInfo();

  /**
   * effects
   */
  useEffect(() => {
    getQuoteRecoveryPageOriginUrl();
  }, []);

  /**
   * Generate premium list:
   * @param type 1.page 2.Send quote dialog
   */
  const generatePremiumLabelItem = (title, desc, extraData?: any) => {
    return {
      title,
      desc,
      titleClassName: 'col-span-6',
      descClassName: 'text-right font-bold col-span-6',
      ...extraData,
    };
  };

  const generatePremiumList = (params?: { type: 1 | 2 }) => {
    const { period = 12, canShowPI, canShowPL, canShowEL, canShowEXL, canShowGIT, canShowCMR, billingCycle } = getValues();
    const {
      piPriceText,
      plPriceText,
      elPriceText,
      exlPriceText,
      gitPriceText,
      cmrPriceText,
      taxText,
      totalText,
      priceText,
      adminFeeText,
      processingFeeText,
    } = quotePriceInfo;
    const { type = 1 } = params;

    // premium price detail info
    let list = [];
    if (type === 1) {
      list.push(generatePremiumLabelItem('Insurance period', `${period} months`));
    }
    if (canShowGIT) {
      list.push(generatePremiumLabelItem(COVER_INFO_MAP[GOODS_IN_TRANSIT].label, gitPriceText));
    }
    if (canShowCMR) {
      list.push(generatePremiumLabelItem(COVER_INFO_MAP[CMR_COVER].label, cmrPriceText));
    }
    if (canShowPI) {
      list.push(generatePremiumLabelItem(COVER_INFO_MAP[PROFESSIONAL_INDEMNITY].label, piPriceText));
    }
    if (canShowPL) {
      list.push(generatePremiumLabelItem(COVER_INFO_MAP[PUBLIC_LIABILITY].label, plPriceText));
    }
    if (canShowEL) {
      list.push(generatePremiumLabelItem(COVER_INFO_MAP[EMPLOYERS_LIABILITY].label, elPriceText));
    }
    if (canShowEXL) {
      list.push(generatePremiumLabelItem(COVER_INFO_MAP[EXCESS_LEVEL].label, exlPriceText));
    }

    // Great American
    if (getValues('isGreateAmerican')) {
      const gaList = generateGreatAmericanPriceSummaryInfoList(quotePriceInfo.covers_price, quotePriceInfo).map((item) => {
        return generatePremiumLabelItem(item.title, item.content);
      });
      list = [...list, ...gaList];
    }

    list = [
      ...list,
      ...[
        generatePremiumLabelItem('Insurance premium', priceText),
        generatePremiumLabelItem('Insurance premium tax (IPT)', taxText),
        generatePremiumLabelItem('Admin fee', adminFeeText),
        generatePremiumLabelItem('Processing fee', processingFeeText),
        generatePremiumLabelItem('Total:', totalText, {
          titleClassName: `font-bold col-span-6 ${type === 1 ? 'text-3xl' : 'text-xl'}`,
          descClassName: `text-right font-bold col-span-6 ${type === 1 ? 'text-3xl' : 'text-xl'}`,
        }),
      ],
    ];
    return list;
  };

  const getQuoteRecoveryPageOriginUrl = () => {
    let originUrl = window.location.origin;
    originUrl = originUrl.replace('//cms.', '//quote.');
    if (originUrl.includes('localhost')) {
      setQuoteRestorePageUrl('http://localhost:3000');
    } else {
      setQuoteRestorePageUrl(originUrl);
    }
  };

  const getQuoteRecoveryPageAddress = (quoteId) => {
    return `${quoteRestorePageUrl}/q/${quoteId}?quoteRecoverySource=${QuoteRecoverySourceEnum.Cms}`;
  };

  const addAndModifyQuoteDetailData = (quoteData) => {
    const { specifyTotalPrice, priceFrom, projectPeriodStart, retroactiveDate, birthDate, status, declarationQuestions, haveNoDotNumber } = quoteData;

    // Dot number
    quoteData.haveDotNumber = !haveNoDotNumber;
    quoteData.haveNoDotNumber = !!haveNoDotNumber;
    // Manual set price
    quoteData.specifyTotalPrice = specifyTotalPrice || '';
    quoteData.priceFrom = priceFrom === PriceFromEnum.MANUAL ? true : false;
    // Policy start date
    quoteData.projectPeriodStart = projectPeriodStart ? dayjs(new Date(projectPeriodStart)) : dayjs(new Date());
    // Retroactive date
    if (retroactiveDate) {
      quoteData.retroactiveDate = dayjs(retroactiveDate);
    }
    // Birth date
    if (birthDate) {
      quoteData.birthDate = dayjs(birthDate);
    }
    // Flag
    quoteData.cannotModifyPrice = !canModifyQuotePrice(status);

    // Set empty string of some field
    ['businessAddress', 'businessAddress2', 'detailedAddress', 'businessPostcode', 'businessCity', 'businessCounty', 'businessState'].forEach(
      (key) => {
        if (!quoteData[key]) quoteData[key] = '';
      }
    );

    if (quoteData.isGreateAmerican) {
      hanleExtraGreateAmericanData(quoteData);
    } else if (quoteData.isRecruitment) {
      hanleExtraRecruitmentData(quoteData);
    } else if (quoteData.isGreateAmericanBussinessInsurance) {
      hanleExtraGreateAmericanBusinessInsuranceData(quoteData);
    }

    // Special case
    if (!!declarationQuestions?.git_move_goods_not_enter_house && !declarationQuestions?.git_move_goods_enter_house) {
      quoteData.declarationQuestions = {
        ...declarationQuestions,
        git_move_goods_enter_house: swapTheValuesOfTheYesAndNoOptions(
          declarationQuestions.git_move_goods_not_enter_house,
          declarationsOptionsMap['2']
        ),
      };
    }

    quoteData.rateAndTotal = !!quoteData.rateAndTotal
      ? quoteData.rateAndTotal
      : {
          incomeType: getChubbContractRateSwitchTypeMap().rate.value,
          income: 0,
          rate: 150,
          perType: ContractEstimatedIncomePerInfoMap.perDay.value,
          perCount: 5,
        };
  };

  const hanleExtraGreateAmericanData = (quoteData) => {
    // Company address
    quoteData.addressInfo = {};
    quoteData.detailedAddress = quoteData.businessAddress2;
    // Type of commodities
    quoteData.commodityList = (quoteData.commodityList || []).map((item) => {
      return {
        value: item.option_id,
        label: item.value,
        count: item.value,
      };
    });
    // Acceptance criteria
    const acceptanceCriteriaSelectCheckedList = [];
    // acceptanceCriteriaInsurance is 'I disagree' here.
    if (quoteData.coverageCancelledOrNonRenewedInLast5Years === YesNoEnum.NO) {
      acceptanceCriteriaSelectCheckedList.push('coverage_cancelled_or_non_renewed_in_last_5_years');
    }
    if (quoteData.currentCarrier === YesNoEnum.NO) {
      acceptanceCriteriaSelectCheckedList.push('current_carrier');
    }
    quoteData.acceptanceCriteriaSelectCheckedList = acceptanceCriteriaSelectCheckedList;

    if (isBoolean(quoteData.isSimpleFlow)) {
      quoteData.declarationQuestions = {
        ...quoteData.declarationQuestions,
        great_american_fast_process_criteria: getDeclarationsOptionsMap()['1'].find((item) => item.truelyValue === quoteData.isSimpleFlow),
      };
    }
    quoteData.typeOfOperation = quoteData.typeOfOperation === 'dryVan' ? greateAmericanTypeOfOperationCourier : quoteData.typeOfOperation;
  };

  const hanleExtraRecruitmentData = (quoteData) => {
    quoteData.placementTypeAndSplitList = (quoteData.placementTypeAndSplitList || []).map((item) => {
      return {
        value: item.placement_type,
        label: item.label || item.placement_type,
        percent: item.value,
      };
    });
    quoteData.ernExempt = quoteData.ernStatus === 'exempt';
    quoteData.covers = {
      ...quoteData.covers,
      [PROPERTY_COVER_TYPE_LIST]: quoteData.covers?.[PROPERTY_COVER_TYPE_LIST] || [],
    };
  };

  const hanleExtraGreateAmericanBusinessInsuranceData = (quoteData) => {
    // Company address
    quoteData.addressInfo = {};
    quoteData.detailedAddress = quoteData.businessAddress2;
    // Quick journey criteria
    if (isBoolean(quoteData.isSimpleFlow)) {
      quoteData.declarationQuestions = {
        ...quoteData.declarationQuestions,
        greate_american_us_quick_journey_criteria: getDeclarationsOptionsMap()['1'].find((item) => item.truelyValue === quoteData.isSimpleFlow),
      };
    }
    quoteData.extraDeclarationSelectOptionList = quoteData?.extraDeclarationSelectOptionList || [];
  };

  const judgeCanEditQuoteData = (data: { status: string }) => {
    return canEditQuoteData(data?.status);
  };

  const getLocalUpdateQuoteDataWithServerQuoteData = (data) => {
    const { status, assumptionFields, flowMode, propertyList, declinedReasons, referralReasons } = data;
    return {
      status,
      assumptionFields,
      flowMode,
      propertyList,
      declinedReasons,
      referralReasons,
    };
  };

  const getQuoteRestoreLinkUrl = (data: { id: string; source?: string; status: string; fastQuoteStates?: string }) => {
    if (!!data.fastQuoteStates && [QuoteStatus.QUOTE_START, QuoteStatus.DROPOUT_BEFORE_QUOTE].includes(data?.status as QuoteStatus)) {
      return `${process.env.NX_WEBSITE_URL}/switch-with-jove/upload-policy-documents?quoteId=${data.id}`;
    }
    return getQuoteRecoveryPageAddress(data?.id);
  };

  return {
    generatePremiumList,
    quoteRestorePageUrl,
    getQuoteRecoveryPageAddress,
    addAndModifyQuoteDetailData,
    judgeCanEditQuoteData,
    getLocalUpdateQuoteDataWithServerQuoteData,
    getQuoteRestoreLinkUrl,
  };
};
